import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "../config/site";
import { useLead } from "../context/LeadContext";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why-us" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Service Areas", href: "#areas" },
  { label: "FAQs", href: "#faq" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { openModal } = useLead();
  const [firstWord, ...rest] = SITE.companyName.split(" ");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 bg-ivory/95 backdrop-blur-md border-b border-border transition-shadow duration-300 ${scrolled ? "shadow-[0_8px_30px_rgba(26,26,26,0.06)]" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 lg:h-20 flex items-center justify-between gap-4">
          <a href="#top" data-testid="logo-link" className="flex items-baseline gap-2 shrink-0" aria-label={`${SITE.companyName} home`}>
            <span className="font-display text-2xl lg:text-[28px] font-semibold text-wine leading-none">{firstWord}</span>
            {rest.length > 0 && <span className="text-[10px] tracking-[0.35em] uppercase text-charcoal/60 font-bold">{rest.join(" ")}</span>}
          </a>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV.map((n) => (
              <a
                key={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                href={n.href}
                className="text-[13px] font-semibold tracking-wide text-charcoal/70 hover:text-wine transition-colors duration-300"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="header-call-btn"
              href={SITE.phoneHref}
              className="hidden md:inline-flex items-center gap-2 text-[13px] font-bold tracking-wide text-charcoal hover:text-wine transition-colors duration-300"
            >
              <Phone className="h-4 w-4" />
              {SITE.phoneDisplay}
            </a>
            <button
              data-testid="header-book-btn"
              onClick={() => openModal()}
              className="hidden sm:inline-flex bg-wine text-ivory px-5 lg:px-6 py-2.5 lg:py-3 text-[12px] font-bold tracking-[0.12em] uppercase hover:bg-wine-light transition-colors duration-300"
            >
              Book Free Inspection
            </button>
            <a data-testid="header-call-icon" href={SITE.phoneHref} aria-label="Call now" className="md:hidden inline-flex items-center justify-center h-10 w-10 border border-border text-wine">
              <Phone className="h-4 w-4" />
            </a>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 border border-border text-charcoal"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div data-testid="mobile-menu" className="fixed inset-0 z-[60] bg-ivory flex flex-col">
          <div className="h-16 px-5 flex items-center justify-between border-b border-border">
            <span className="font-display text-2xl font-semibold text-wine">{firstWord}</span>
            <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="h-10 w-10 inline-flex items-center justify-center border border-border">
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
            {NAV.map((n, i) => (
              <a
                key={n.href}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                href={n.href}
                onClick={() => setMenuOpen(false)}
                className="font-display text-4xl font-medium text-charcoal py-3 border-b border-border/60 hover:text-wine transition-colors"
              >
                <span className="text-sm text-gold italic mr-3">0{i + 1}</span>
                {n.label}
              </a>
            ))}
          </nav>
          <div className="p-6 border-t border-border">
            <button
              data-testid="mobile-menu-book-btn"
              onClick={() => { setMenuOpen(false); openModal(); }}
              className="w-full bg-wine text-ivory py-4 text-[13px] font-bold tracking-[0.14em] uppercase"
            >
              Book Free Inspection
            </button>
          </div>
        </div>
      )}
    </>
  );
};
